import React, { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { useTranslation } from '../../../app/LanguageContext';
import { submitImprovementSuggestion } from '../../../app/api';

const SuggestImprovementButton = ({ pathname, pageTitle, workspaceId }) => {
  const { translate } = useTranslation();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState('');
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState('');
  const [extraImageBlob, setExtraImageBlob] = useState(null);
  const [extraPreviewUrl, setExtraPreviewUrl] = useState('');
  const [buttonHidden, setButtonHidden] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const pasteAreaRef = useRef(null);

  const revokePreviewUrls = useCallback(() => {
    if (screenshotPreviewUrl) URL.revokeObjectURL(screenshotPreviewUrl);
    if (extraPreviewUrl) URL.revokeObjectURL(extraPreviewUrl);
  }, [screenshotPreviewUrl, extraPreviewUrl]);

  const resetForm = useCallback(() => {
    revokePreviewUrls();
    setDescription('');
    setScreenshotBlob(null);
    setScreenshotPreviewUrl('');
    setExtraImageBlob(null);
    setExtraPreviewUrl('');
  }, [revokePreviewUrls]);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  useEffect(() => () => revokePreviewUrls(), [revokePreviewUrls]);

  const captureScreenshot = async () => {
    setButtonHidden(true);
    await new Promise((resolve) => requestAnimationFrame(resolve));
    try {
      const canvas = await html2canvas(document.body, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: null,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) resolve(result);
          else reject(new Error('Failed to encode screenshot'));
        }, 'image/png', 0.92);
      });
      return blob;
    } finally {
      setButtonHidden(false);
    }
  };

  const handleOpen = async () => {
    setCapturing(true);
    try {
      const blob = await captureScreenshot();
      revokePreviewUrls();
      setScreenshotBlob(blob);
      setScreenshotPreviewUrl(URL.createObjectURL(blob));
      setExtraImageBlob(null);
      setExtraPreviewUrl('');
      setDescription('');
      setOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || translate('improvement_suggestion.capture_error'),
        severity: 'error',
      });
    } finally {
      setCapturing(false);
    }
  };

  const handlePaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (!file) continue;
      event.preventDefault();
      if (extraPreviewUrl) URL.revokeObjectURL(extraPreviewUrl);
      setExtraImageBlob(file);
      setExtraPreviewUrl(URL.createObjectURL(file));
      break;
    }
  };

  const handleSubmit = async () => {
    if (!description.trim() || !screenshotBlob) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('description', description.trim());
      formData.append('page_path', pathname || '/');
      formData.append('page_title', pageTitle || '');
      if (workspaceId) {
        formData.append('workspace_id', workspaceId);
      }
      formData.append('screenshot', screenshotBlob, 'screenshot.png');
      if (extraImageBlob) {
        formData.append('extra_image', extraImageBlob, 'extra.png');
      }
      await submitImprovementSuggestion(formData);
      setSnackbar({
        open: true,
        message: translate('improvement_suggestion.submit_success'),
        severity: 'success',
      });
      setOpen(false);
      resetForm();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || translate('improvement_suggestion.submit_error'),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!buttonHidden && (
        <Tooltip title={translate('improvement_suggestion.button_tooltip')} placement="left">
          <Fab
            size="small"
            color="default"
            aria-label={translate('improvement_suggestion.button_tooltip')}
            onClick={handleOpen}
            disabled={capturing}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: (theme) => theme.zIndex.speedDial,
              opacity: 0.85,
              boxShadow: 2,
            }}
          >
            {capturing ? <CircularProgress size={22} color="inherit" /> : <FeedbackOutlinedIcon fontSize="small" />}
          </Fab>
        </Tooltip>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('improvement_suggestion.dialog_title')}</DialogTitle>
        <DialogContent dividers sx={{ overflow: 'auto' }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={20}
            margin="normal"
            label={translate('improvement_suggestion.description_label')}
            placeholder={translate('improvement_suggestion.description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {translate('improvement_suggestion.screenshot_label')}
          </Typography>
          {screenshotPreviewUrl && (
            <Box
              component="img"
              src={screenshotPreviewUrl}
              alt={translate('improvement_suggestion.screenshot_label')}
              sx={{
                width: '100%',
                maxHeight: 220,
                objectFit: 'contain',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            />
          )}

          <Box
            ref={pasteAreaRef}
            tabIndex={0}
            onPaste={handlePaste}
            sx={{
              mt: 2,
              p: 2,
              border: 1,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 1,
              outline: 'none',
              '&:focus-visible': {
                borderColor: 'primary.main',
              },
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {translate('improvement_suggestion.extra_image_label')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('improvement_suggestion.extra_image_hint')}
            </Typography>
            {extraPreviewUrl && (
              <Box
                component="img"
                src={extraPreviewUrl}
                alt={translate('improvement_suggestion.extra_image_label')}
                sx={{
                  mt: 1,
                  width: '100%',
                  maxHeight: 160,
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {translate('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !description.trim() || !screenshotBlob}
          >
            {submitting ? translate('improvement_suggestion.submitting') : translate('improvement_suggestion.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SuggestImprovementButton;
